"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.printerReceiptConfigPutBodySchema = exports.printerReceiptConfigSchema = exports.DEFAULT_BUSINESS_DETAIL_STYLES = exports.businessDetailsOverrideSchema = exports.MAX_TOTAL_BLOCKS = exports.receiptBlockSchema = void 0;
const zod_1 = require("zod");
const align = zod_1.z.enum(['L', 'C', 'R']);
const size4 = zod_1.z.enum(['S', 'N', 'L', 'XL']);
const size3 = zod_1.z.enum(['S', 'M', 'L']);
const spacingAbove = zod_1.z.enum(['none', 'S', 'L']);
const dividerStyle = zod_1.z.enum(['dashed', 'solid', 'double']);
const printerWidth = zod_1.z.enum(['80mm', '58mm']);
const blockId = zod_1.z.string().min(1).max(40);
const textBlock = zod_1.z.object({
    id: blockId,
    type: zod_1.z.literal('text'),
    content: zod_1.z.string().max(200),
    size: size4,
    align,
    bold: zod_1.z.boolean(),
    italic: zod_1.z.boolean(),
    underline: zod_1.z.boolean(),
    inverse: zod_1.z.boolean(),
    spacingAbove,
});
const boxedBlock = zod_1.z.object({
    id: blockId,
    type: zod_1.z.literal('boxed'),
    content: zod_1.z.string().max(200),
    size: size4,
    align,
    bold: zod_1.z.boolean(),
    borderStyle: dividerStyle,
    spacingAbove,
});
const logoBlock = zod_1.z.object({
    id: blockId,
    type: zod_1.z.literal('logo'),
    align,
    maxWidth: size3,
    spacingAbove,
});
// Note: imageUrl and qr.data accept empty strings so users can save WIP designs.
// Renderers and the printer transport skip blocks whose URL/data is empty.
// CDN-allowlist validation still applies to non-empty image URLs (in pos-register-service validator).
const imageBlock = zod_1.z.object({
    id: blockId,
    type: zod_1.z.literal('image'),
    imageUrl: zod_1.z.string().max(500),
    align,
    maxWidth: size3,
    spacingAbove,
});
const qrBlock = zod_1.z.object({
    id: blockId,
    type: zod_1.z.literal('qr'),
    data: zod_1.z.string().max(500),
    align,
    size: size3,
    caption: zod_1.z.string().max(100).nullable(),
    spacingAbove,
});
const dividerBlock = zod_1.z.object({
    id: blockId,
    type: zod_1.z.literal('divider'),
    style: dividerStyle,
    spacingAbove,
});
const spacerBlock = zod_1.z.object({
    id: blockId,
    type: zod_1.z.literal('spacer'),
    height: size3,
});
exports.receiptBlockSchema = zod_1.z.discriminatedUnion('type', [
    textBlock, boxedBlock, logoBlock, imageBlock, qrBlock, dividerBlock, spacerBlock,
]);
const slotsSchema = zod_1.z.object({
    header: zod_1.z.array(exports.receiptBlockSchema),
    belowItems: zod_1.z.array(exports.receiptBlockSchema),
    footer: zod_1.z.array(exports.receiptBlockSchema),
});
exports.MAX_TOTAL_BLOCKS = 20;
const blockCapRefine = (cfg) => cfg.slots.header.length + cfg.slots.belowItems.length + cfg.slots.footer.length <= exports.MAX_TOTAL_BLOCKS;
const blockCapMessage = { message: `total blocks across slots cannot exceed ${exports.MAX_TOTAL_BLOCKS}` };
// Per-line override for the (otherwise locked) business-details section.
// Each line carries optional `content` (overrides location default if non-empty)
// and optional style fields (size / align / bold / italic / underline) — missing
// style fields fall back to DEFAULT_BUSINESS_DETAIL_STYLES.
const businessDetailLineSchema = zod_1.z.object({
    content: zod_1.z.string().max(120).nullable().optional(),
    size: zod_1.z.enum(['S', 'N', 'L', 'XL']).optional(),
    align: zod_1.z.enum(['L', 'C', 'R']).optional(),
    bold: zod_1.z.boolean().optional(),
    italic: zod_1.z.boolean().optional(),
    underline: zod_1.z.boolean().optional(),
}).nullable().optional();
// Address splits into up to 3 separate lines. Empty lines are skipped on print
// so a tenant can choose to use 1, 2, or 3 lines as fits their format.
exports.businessDetailsOverrideSchema = zod_1.z.object({
    legalName: businessDetailLineSchema,
    addressLine1: businessDetailLineSchema,
    addressLine2: businessDetailLineSchema,
    addressLine3: businessDetailLineSchema,
    phone: businessDetailLineSchema,
}).nullable().optional();
// Default style applied to a line when the override doesn't specify it.
exports.DEFAULT_BUSINESS_DETAIL_STYLES = {
    legalName: { size: 'L', align: 'C', bold: true, italic: false, underline: false },
    addressLine1: { size: 'N', align: 'C', bold: false, italic: false, underline: false },
    addressLine2: { size: 'N', align: 'C', bold: false, italic: false, underline: false },
    addressLine3: { size: 'N', align: 'C', bold: false, italic: false, underline: false },
    phone: { size: 'N', align: 'C', bold: false, italic: false, underline: false },
};
const printerReceiptConfigBaseSchema = zod_1.z.object({
    tenantId: zod_1.z.string().min(1),
    locationId: zod_1.z.string().min(1),
    logoUrl: zod_1.z.string().url().max(500).nullable(),
    printerWidth,
    businessDetailsOverride: exports.businessDetailsOverrideSchema,
    slots: slotsSchema,
    version: zod_1.z.number().int().nonnegative(),
    updatedAt: zod_1.z.number().int().nonnegative(),
    updatedBy: zod_1.z.string().min(1),
});
exports.printerReceiptConfigSchema = printerReceiptConfigBaseSchema.refine(blockCapRefine, blockCapMessage);
// Body of PUT request — server fills in tenantId, version, updatedAt, updatedBy
exports.printerReceiptConfigPutBodySchema = printerReceiptConfigBaseSchema
    .omit({ tenantId: true, locationId: true, version: true, updatedAt: true, updatedBy: true })
    .refine(blockCapRefine, blockCapMessage);
//# sourceMappingURL=printer-receipt.schema.js.map